import { notify } from "@/components/Shares/Notification";
import NumberSelector from "@/components/Shares/NumberSelector";
import EStateAsyncStatus from "@/constants/stateAsyncState";
import { useAppDispatch, useAppSelector } from "@/hooks/useRedux";
import { IProductVariant } from "@/interfaces/product";
import { cart, fetchAddCart, openCart } from "@/store/slices/cartSlice";
import { formatPrice } from "@/utils/format";
import { Button, Checkbox } from "@nextui-org/react";
import { isEmpty, isEqual } from "lodash";
import Link from "next/link";
import { useState } from "react";

interface QuickInfoProps {
  productVariants: IProductVariant[];
  selectedVariant: IProductVariant;
  setSelectedVariant: (value: IProductVariant) => void;
  link: string;
}

const QuickInfo: React.FC<QuickInfoProps> = (props) => {
  const { productVariants, selectedVariant, setSelectedVariant, link } = props;
  const [productNum, setProductNum] = useState(
    selectedVariant.totalInventory === 0 ? 0 : 1,
  );
  const [isAssembly, setIsAssembly] = useState(false);
  const dispatch = useAppDispatch();
  const cartState = useAppSelector(cart);

  const handleAddToCart = async () => {
    if (
      isEqual(cartState.status, EStateAsyncStatus.succeeded) &&
      cartState.cart
    ) {
      if (productNum <= 0) {
        notify("Failed to add to cart, please select quantity", "error");
        return;
      }
      const payload = {
        productVariantId: selectedVariant.id,
        quantity: productNum,
        shoppingCartId: cartState.cart.id,
        isAssembly,
      };
      await dispatch(fetchAddCart(payload));
      dispatch(openCart());
    }
  };

  return (
    <div className="flex flex-col">
      <Link href={link}>
        <h2 className="text-lg hover:text-icon transition-all duration-500">
          {selectedVariant.name}
        </h2>
      </Link>
      <div className="flex gap-4 text-sm">
        <p>
          {selectedVariant.totalInventory === 0
            ? "Out of Stock"
            : `${selectedVariant.totalInventory} In Stock`}{" "}
        </p>
        <p>SKU: {selectedVariant.sku}</p>
      </div>
      <div className="flex text-3xl mt-4 mb-6 gap-2">
        <p className="text-red-500">{formatPrice(selectedVariant.price)}</p>
        {selectedVariant.oldPrice !== 0 && (
          <div className="flex gap-2">
            <p className={"line-through text-gray-500 font-light"}>
              <span>{formatPrice(selectedVariant.oldPrice)}</span>
            </p>
            <div className="bg-red-500 text-white px-2 rounded-md">
              <p>
                -
                {Math.round(
                  ((selectedVariant.oldPrice - selectedVariant.price) * 100) /
                    selectedVariant.oldPrice,
                )}
                %
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-4 items-center mb-6">
        <p>Color</p>
        <div className="flex gap-1">
          {!isEmpty(productVariants) &&
            productVariants.map((variant) => {
              const { id, color } = variant;
              return (
                <div
                  key={id}
                  style={{ backgroundColor: `${color}` }}
                  className={`size-8 border rounded-md cursor-pointer hover:border-blue-400 select-none ${
                    isEqual(selectedVariant.id, id)
                      ? "border-blue-600 border-[2.5px]"
                      : "border-gray-300"
                  }`}
                  onClick={() => setSelectedVariant(variant)}
                />
              );
            })}
        </div>
      </div>
      {selectedVariant.canAssembly && (
        <Checkbox
          isSelected={isAssembly}
          onValueChange={setIsAssembly}
          color="warning"
          radius="none"
          aria-label="Assembly"
        >
          Assembly is available only for delivery service. Do you want us to
          assemble the product upon delivery? [+
          {formatPrice(selectedVariant.assemblyFee)}]
        </Checkbox>
      )}
      <div className="flex items-center gap-4 mt-10">
        <NumberSelector
          value={productNum}
          setValue={setProductNum}
          maxNumber={selectedVariant.totalInventory}
        />
        <Button
          className="bg-icon w-full text-lg text-white py-6 rounded-full shadow-card transition-all duration-500"
          onClick={handleAddToCart}
        >
          Add to Cart •
          {`${formatPrice(
            selectedVariant.price +
              (isAssembly ? selectedVariant.assemblyFee : 0),
          )}`}
        </Button>
      </div>
    </div>
  );
};

export default QuickInfo;
